import type { Sheet } from '../../types';
import { getPouchesApiUrl } from '../../utils/pouchesApiUrl';
import { normalizeSheetColors } from './normalizeSheetColors';
import { stripSheetUiFlags } from './stripUiFlags';
import type { SheetApiError } from './types';

export type ShareVisibility = 'private' | 'public';

export type ShareSheetResult = {
  ok: true;
  sheetId: string;
  visibility: ShareVisibility;
  fileName: string;
  url: string;
  expiresAt: number | null;
};

export type DownloadSheetResult = {
  ok: true;
  sheetId: string;
  fileName: string;
  mimeType: 'application/pouches';
  content: string;
};

const SHARE_BASE_URL = 'https://apps.tomippe.jp/pouches/run/';
const DEFAULT_TITLE = 'untitled';

/** 共有・ダウンロード用の安全な .pouches ファイル名 */
export function buildPouchesFileName(title?: string): string {
  const raw = (title ?? '').trim() || DEFAULT_TITLE;
  const sanitized = raw
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, ' ')
    .slice(0, 120)
    .trim();
  const base = sanitized || DEFAULT_TITLE;
  return base.toLowerCase().endsWith('.pouches') ? base : `${base}.pouches`;
}

function prepareExportSheet(
  sheet: Sheet,
  options: { includeHistory: boolean },
): Sheet {
  const cleaned = stripSheetUiFlags(sheet);
  const normalized = normalizeSheetColors(cleaned);
  if (options.includeHistory) {
    return normalized;
  }
  return {
    ...normalized,
    history: [],
    historyIndex: 0,
  };
}

/** `.pouches` ファイル本文（`{ sheets: [...] }`）を組み立てる */
export function buildPouchesFileContent(
  sheet: Sheet,
  options: { includeHistory: boolean },
): { fileName: string; content: string; mimeType: 'application/pouches' } {
  const exportSheet = prepareExportSheet(sheet, options);
  const fileName = buildPouchesFileName(exportSheet.title);
  const content = JSON.stringify({ sheets: [exportSheet] }, null, 2);
  return {
    fileName,
    content,
    mimeType: 'application/pouches',
  };
}

export function buildShareUrl(
  serverFileName: string,
  visibility: ShareVisibility,
): string {
  const param = visibility === 'public' ? 'import_public' : 'import';
  return `${SHARE_BASE_URL}?${param}=${encodeURIComponent(serverFileName)}`;
}

/** 一時共有用にサーバーへアップロードし、共有 URL を返す */
export async function uploadSheetForShare(
  jsonData: string,
  originalFileName: string,
  visibility: ShareVisibility,
): Promise<
  | { ok: true; fileName: string; url: string; expiresAt: number | null }
  | SheetApiError
> {
  try {
    const formData = new FormData();
    const blob = new Blob([jsonData], { type: 'application/pouches' });
    const uploadName = `${Date.now()}_${Math.random().toString(36).slice(2, 15)}.pouches`;
    formData.append('file', blob, uploadName);
    formData.append('originalFileName', originalFileName);
    formData.append('isPublic', String(visibility === 'public'));

    const response = await fetch(getPouchesApiUrl('/api/upload-temp.php'), {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      return {
        ok: false,
        code: 'SHARE_UPLOAD_FAILED',
        message: errorText || `Share upload failed (${response.status})`,
      };
    }

    const result = (await response.json()) as {
      fileName?: string;
      expiresAt?: number;
    };
    const fileName = result.fileName || uploadName;
    const expiresAt =
      visibility === 'public'
        ? null
        : typeof result.expiresAt === 'number'
          ? result.expiresAt
          : null;

    return {
      ok: true,
      fileName,
      url: buildShareUrl(fileName, visibility),
      expiresAt,
    };
  } catch (error) {
    return {
      ok: false,
      code: 'SHARE_UPLOAD_FAILED',
      message: error instanceof Error ? error.message : String(error),
    };
  }
}

export function downloadSheetForApi(sheet: Sheet): DownloadSheetResult {
  const built = buildPouchesFileContent(sheet, { includeHistory: true });
  return {
    ok: true,
    sheetId: sheet.id,
    fileName: built.fileName,
    mimeType: built.mimeType,
    content: built.content,
  };
}

export async function shareSheetForApi(
  sheet: Sheet,
  visibility: ShareVisibility,
): Promise<ShareSheetResult | SheetApiError> {
  const built = buildPouchesFileContent(sheet, { includeHistory: false });
  const uploaded = await uploadSheetForShare(
    built.content,
    built.fileName,
    visibility,
  );
  if (!uploaded.ok) {
    return uploaded;
  }
  return {
    ok: true,
    sheetId: sheet.id,
    visibility,
    fileName: uploaded.fileName,
    url: uploaded.url,
    expiresAt: uploaded.expiresAt,
  };
}
